// @flow

import React, { Component, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import {
  Button,
  DropdownButton,
  Hint,
  Link,
  StickyScrollParent,
  StickyScrollElement,
  StickyScrollElementMain,
  StickyScrollElementFixed,
  MediaQuery,
} from '@riseart/common';
import { ClockIcon, PhoneIcon, TickIcon } from '@riseart/icons';
import {
  ArtAction,
  ArtActionDetails,
  ArtActionRow,
  ArtActionInfo,
  ArtActionInfoHighlighted,
  ArtActionInfoCommon,
  ArtActionPrice,
  ArtActionCheckout,
  ArtActionRent,
  ArtActionButton,
  ArtActionEnquire,
  ArtOptionFrame,
  ArtActionSplitRow,
  ArtFrameDescription,
} from '@riseart/art';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { application as CONFIG_APP } from 'Config';
import { product as PRODUCT_ENUM, art as ART_ENUM } from 'Enum';
import { LocalizedConfig } from 'shared_services/riseart/utils/LocalizedConfig';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { formatCurrency } from 'shared_services/riseart/utils/Utils';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { AddCartItemButton } from 'shared_data/providers/queries/AddCartItem';
import { Share } from 'shared_components/common/share/Share';
import { EnquireOverlay } from 'shared_components/enquire/Overlay';
import { EnquireArtworkForm } from 'shared_components/forms/enquire/Artwork';
import { EnquireOfferForm } from 'shared_components/forms/enquire/Offer';
import { EnquireCommissionForm } from 'shared_components/forms/enquire/Commission';
import { ShipsFrom } from 'shared_components/arts/fragments/ShipsFrom';
import { ReviewsLogo } from 'shared_components/arts/reviews/Logo';
import { FavoriteButton } from 'shared_data/providers/queries/FavoriteButton';
import { TRANSLATIONS as FRAME_TRANSLATIONS } from 'shared_components/arts/translations/frames';
import { translateFrameOption } from 'shared_services/riseart/utils/artDataUtils';

import { raScreenSmMax, raScreenMd } from '@riseart/antd-provider/dist/website/variables.less';
import {
  artPurchaseRowCls,
  artLinkSeparatorCls,
  artLinkDropdownWishlistCls,
} from 'shared_components/arts/detail/Detail.less';

const { signs: CURRENCY_SIGNS } = CONFIG_APP.i18n.currency;

export function normalizeData(
  rawData: Object,
  storeCode: string | null | void,
  formatMessage: Function,
  localeCode: string,
) {
  const { artist, medium, productType, productSkus, productPrimarySku } = rawData;
  const primarySku = productPrimarySku || {};
  const productStore = find(primarySku.stores, { storeCode }) || {};
  const rentDetails = find(productSkus, { type: 'REN' }) || {};
  const { id: frameOptionsId, values: frameValues = [] } =
    find(primarySku.options, { isFrame: true, storeCode }) || {};
  const frames: Array<Object> = frameValues.map(({ id, price, title }) => {
    return {
      ...translateFrameOption(title, formatMessage),
      id: parseInt(id, 10),
      price,
      priceLabel: formatCurrency(price, storeCode, null, {
        locale: localeCode,
      }),
    };
  });

  // rent data
  const insuranceOption = find(rentDetails.options, { isInsurance: true }) || {};
  const insuranceOptionStore = find(insuranceOption.values, { storeCode }) || {};
  const rentData = {
    rentDetailsId: parseInt(rentDetails.id, 10),
    insuranceOptionStore,
  };

  return {
    artist,
    medium: formatMessage({ id: `filters.art.medium.${medium}` }),
    isLimitedEdition: productType === PRODUCT_ENUM.type.TYPE_LIMITED_EDITION,
    editionSize: primarySku.editionSize,
    canBeFramed: primarySku.canBeFramed,
    canBuyInInstalments: primarySku.canBuyInInstalments,
    isFramed: primarySku.framed,
    readyToHang: primarySku.readyToHang,
    primarySkuProductId: primarySku.id,
    primarySku,
    rentDetails,
    insuranceOption,
    frames,
    frameOptionsId: frameOptionsId ? parseInt(frameOptionsId, 10) : null,
    productStore,
    currencySign: CURRENCY_SIGNS[productStore.storeCode] || '',
    rentData,
  };
}

export function definePurchaseProps(
  preparedData: Object,
  selectedFrame: Object = {},
  localeCode: string,
): Object {
  const {
    primarySkuProductId,
    canBuyInInstalments,
    productStore,
    currencySign,
    frameOptionsId,
    isFramed,
    canBeFramed,
    readyToHang,
    frames,
  } = preparedData;

  const framePrice = (selectedFrame && selectedFrame.price && parseFloat(selectedFrame.price)) || 0;

  return {
    price: formatCurrency(productStore.price + framePrice, undefined, currencySign, {
      locale: localeCode,
    }),
    canBuyInInstalments,
    priceInInstalments:
      canBuyInInstalments && productStore.priceInInstalments
        ? formatCurrency(productStore.priceInInstalments, undefined, currencySign, {
            locale: localeCode,
          })
        : null,
    productId: parseInt(primarySkuProductId, 10),
    frameOptionsId,
    selectedFrameId: (selectedFrame && selectedFrame.id) || '',
    buttonText: <FormattedMessage id="components.art.add_to_basket" />,
    frameOptions: { canBeFramed, isFramed, readyToHang, frames },
    options:
      selectedFrame && selectedFrame.id && frameOptionsId
        ? [{ id: frameOptionsId, values: [selectedFrame.id] }]
        : null,
  };
}

type Props = {
  data: Object,
  urgencyData: { heading: string, content: string } | null,
  storeCode: string,
  localeCode: string,
  shippingCountryCode?: string,
  onFrameSelect: Function,
  intl: Object,
  location: Object,
  history: Object,
  handleReviewLinkClick: Function,
};

type State = {
  storeCode: string,
  itemId: number | string,
  selectedFrame: Object,
};

/**
 * ArtDetailsComponent
 */
class ArtDetailsComponent extends Component<Props, State> {
  handleClick: Function;

  /**
   * constructor
   * @param {Props} props
   */
  constructor(props: Props) {
    super(props);

    this.bindMethods();
    this.state = {
      // eslint-disable-next-line
      storeCode: props.storeCode,
      // eslint-disable-next-line
      itemId: props.data.id,
      selectedFrame: {},
    };
  }

  /**
   * getDerivedStateFromProps
   * @param {Props} props
   * @param {State} state
   */
  static getDerivedStateFromProps(props: Props, state: State) {
    if (state.itemId !== props.data.id || state.storeCode !== props.storeCode) {
      return {
        selectedFrame: {},
        storeCode: props.storeCode,
        itemId: props.data.id,
      };
    }

    return null;
  }

  /**
   * bindMethods
   */
  bindMethods() {
    this.handleClick = this.handleClick.bind(this);
  }

  /**
   * handleClick
   * @param {?Object} frame
   * @returns {Function}
   */
  handleClick(frame: ?Object = {}): Function {
    this.props.onFrameSelect && this.props.onFrameSelect(frame);
    this.setState({ selectedFrame: frame });
  }

  /**
   * rentProps
   *
   * @param {Object} preparedData
   * @param {string}storeCode
   * @returns {Object}
   */
  rentProps(preparedData: Object, storeCode: string, localeCode: string): Object {
    const { rentDetails, insuranceOption, currencySign } = preparedData;
    const rentStoreDetails: Object = find(get(rentDetails, 'stores', []), { storeCode });
    const insuranceOptionStore: Object = find(insuranceOption.values, { storeCode }) || {};

    return {
      rentDetailsId: parseInt(rentDetails.id, 10),
      insuranceOptionId: insuranceOption.id,
      insuranceOptionStoreId: insuranceOptionStore.id,
      options: insuranceOption.id
        ? [
            {
              id: parseInt(insuranceOption.id, 10),
              values: [parseInt(insuranceOptionStore.id, 10)],
            },
          ]
        : null,
      buttonText: (
        <FormattedMessage
          id="components.art.rent_monthly"
          values={{
            rentPrice: formatCurrency(rentStoreDetails.price, undefined, currencySign, {
              locale: localeCode,
            }),
          }}
        />
      ),
    };
  }

  renderAdditionalInfo({
    isProduct,
    productIsEnquire,
    productIsRented,
    productInStore,
    productInStores = [],
    statusMessage,
  }: Object) {
    if (!isProduct) {
      return <FormattedMessage id="components.art.unavailable_to_buy" />;
    }
    if (productIsEnquire) {
      return statusMessage || <FormattedMessage id="components.art.is_enquire" />;
    }
    if (productIsRented) {
      return <FormattedMessage id="components.art.is_rented" />;
    }
    if (!productInStore && productInStores.length) {
      const { intl } = this.props;
      const totalStores = productInStores.length;
      const lastIndex = totalStores - 1;
      const availableCurrencies = productInStores.reduce((acc, i, idx) => {
        const translatedCurrency = intl.formatMessage({ id: `stores.${i}` });

        if (idx === 0) {
          acc += translatedCurrency;
        } else if (idx === lastIndex) {
          acc += ` ${intl.formatMessage({ id: 'common.or' })} ${translatedCurrency}`;
        } else {
          acc += `, ${translatedCurrency}`;
        }

        return acc;
      }, '');

      return (
        <FormattedMessage
          id="components.art.unavailable_in_store"
          values={{ availableCurrencies, totalStores }}
        >
          {(translatedText: string) => translatedText}
        </FormattedMessage>
      );
    }

    return <FormattedMessage id="components.art.sold_out" />;
  }

  /**
   * render
   * @returns {React$Element<any>}
   */
  render() {
    const {
      data,
      urgencyData,
      storeCode,
      localeCode,
      shippingCountryCode,
      handleReviewLinkClick,
      intl,
      location,
      history,
    } = this.props;
    const {
      id: artworkId,
      title,
      url,
      artist,
      statusMessage,
      isProduct,
      productCanBuy,
      productCanRent,
      productCanOffer,
      productId,
      productInStore,
      productInStores,
      productIsEnquire,
      productIsRented,
      productIsSoldOut,
      productPrimarySku,
      artistName,
      slug,
      dimensions,
      sharedImage,
    } = data;

    if (isEmpty(this.props.data)) {
      return null;
    }

    const learnMoreLabel = intl.formatMessage({ id: 'common.learnMore' });
    const learnHowLabel = intl.formatMessage({ id: 'components.art.commissionLabelAction' });
    const preparedData = {
      ...normalizeData(this.props.data, storeCode, intl.formatMessage, localeCode),
      url,
      productId,
    };
    const showEnquireButton =
      !isProduct || (!productCanBuy && !productCanRent && (productIsEnquire || productIsRented));
    const purchaseProps =
      (productCanBuy && definePurchaseProps(preparedData, this.state.selectedFrame, localeCode)) ||
      {};
    const CheckoutSection = productCanBuy ? (
      <ArtActionPrice
        price={purchaseProps.price}
        canBuyInInstalments={purchaseProps.canBuyInInstalments}
      />
    ) : null;
    const BuyButton = productCanBuy ? (
      <AddCartItemButton
        cartId="buy"
        qty={1}
        skuId={purchaseProps.productId}
        options={purchaseProps.options}
      >
        {({ loading, onClick }) => {
          return <ArtActionCheckout {...purchaseProps} onClick={onClick} loading={loading} />;
        }}
      </AddCartItemButton>
    ) : null;
    const artworkUrl = UrlAssembler.artDetail(artworkId, slug, {
      origin: LocationManager.get('origin'),
    });
    const enquireOverlayTitle = intl.formatMessage({ id: 'forms.emailEnquire.overlayTitle' });
    const rentProps =
      (productCanRent && this.rentProps(preparedData, storeCode, localeCode)) || null;
    const showCommission = !showEnquireButton && !productCanBuy && artist.commissions;
    const offerHash = LocalizedConfig.get('navigation.uri.hash.params.offer');
    const ArtActionSection = (
      <ArtAction>
        <ArtActionDetails
          title={title}
          artistName={artistName}
          artistUrl={artist && UrlAssembler.artistProfile(artist.id, artist.alias)}
          dimensions={dimensions}
          additionalText={
            preparedData.isLimitedEdition ? (
              <FormattedMessage
                id="components.art.edition_of"
                values={{ editionSize: preparedData.editionSize }}
              />
            ) : (
              preparedData.medium
            )
          }
          hint={Hint}
          links={
            <React.Fragment>
              {showEnquireButton || productCanBuy ? null : (
                <Fragment>
                  <FavoriteButton artId={artworkId} type="semitransparent" />
                  <span className={artLinkSeparatorCls}>|</span>
                </Fragment>
              )}
              <EnquireOverlay
                title={enquireOverlayTitle}
                component={({ onClose }) => (
                  <EnquireArtworkForm
                    onClose={onClose}
                    artwork={data}
                    selectedFrame={this.state.selectedFrame}
                    trigger={ART_ENUM.enquire.trigger.TRIGGER_HELP_LINK}
                  />
                )}
              >
                <Link
                  external
                  type="light"
                  title={intl.formatMessage({ id: 'components.art.need_help.title' })}
                  href={LocalizedConfig.get('externalUrls.talkToCurator')}
                >
                  <PhoneIcon />
                </Link>
              </EnquireOverlay>
              <span className={artLinkSeparatorCls}>|</span>
              <Share
                contentKey="artwork_detail"
                contentValues={{
                  id: artworkId,
                  name: title,
                  artist: artistName,
                  url: artworkUrl,
                  image: sharedImage,
                }}
                theme="dark"
                image={sharedImage}
                url={artworkUrl}
              />
            </React.Fragment>
          }
          translations={{
            by: <FormattedMessage id="components.art.by" />,
            artworkSizeTooltipHeading: (
              <FormattedMessage id="components.art.size_tooltip_heading" />
            ),
            artworkSizeTooltipDescription: (
              <FormattedMessage id="components.art.size_tooltip_description" />
            ),
          }}
        >
          <ArtFrameDescription
            {...purchaseProps.frameOptions}
            translations={FRAME_TRANSLATIONS}
            hint={Hint}
          />
        </ArtActionDetails>
        {/* Frame Section */}
        {productCanBuy ? (
          <ArtOptionFrame
            controlled
            options={purchaseProps.frameOptions}
            selectedFrameId={this.state.selectedFrame.id}
            onFrameChange={this.handleClick}
            translations={FRAME_TRANSLATIONS}
            hint={Hint}
          />
        ) : null}
        {productCanBuy ? (
          <ArtActionSplitRow>
            {CheckoutSection}
            <div>
              <Button type="link" linkType="light" onClick={handleReviewLinkClick}>
                <ReviewsLogo />
              </Button>
            </div>
          </ArtActionSplitRow>
        ) : null}
        {/* Status/Additional Information */}
        {(!isProduct || (!productCanBuy && !productCanRent)) && (
          <ArtActionSplitRow>
            <strong>
              {this.renderAdditionalInfo({
                isProduct,
                productIsSoldOut,
                productIsEnquire,
                productIsRented,
                storeCode,
                productInStore,
                productInStores,
                statusMessage,
              })}
            </strong>
            <div>
              <Button type="link" linkType="light" onClick={handleReviewLinkClick}>
                <ReviewsLogo align="vertical" />
              </Button>
            </div>
          </ArtActionSplitRow>
        )}
        <ArtActionRow>
          <DropdownButton
            mainButton={BuyButton}
            dropdownOption={
              productCanRent && (
                <AddCartItemButton
                  cartId="rent"
                  qty={1}
                  skuId={rentProps.rentDetailsId}
                  options={rentProps.options}
                >
                  {({ loading, onClick }) => {
                    return <ArtActionRent {...rentProps} onClick={onClick} loading={loading} />;
                  }}
                </AddCartItemButton>
              )
            }
          />
        </ArtActionRow>
        {productCanBuy && (
          <ArtActionRow>
            {productCanOffer ? (
              <DropdownButton
                mainButton={
                  <FormattedMessage id="components.art.makeOffer">
                    {(makeOfferLabel: string) => (
                      <EnquireOverlay
                        onToggle={(isOpened) => {
                          history.replace(
                            [
                              location.pathname,
                              location.search,
                              ...(isOpened ? [`#${offerHash}`] : []),
                            ].join(''),
                          );
                        }}
                        title={makeOfferLabel}
                        open={location && location.hash === `#${offerHash}`}
                        component={({ onClose }) => (
                          <EnquireOfferForm
                            onClose={onClose}
                            storeCode={storeCode}
                            artwork={data}
                            price={preparedData.productStore.price}
                            selectedFrame={this.state.selectedFrame}
                            trigger={ART_ENUM.enquire.trigger.TRIGGER_OFFER_BUTTON}
                          />
                        )}
                      >
                        <ArtActionButton type="secondary" size="medium" width="container">
                          {makeOfferLabel}
                        </ArtActionButton>
                      </EnquireOverlay>
                    )}
                  </FormattedMessage>
                }
                secondaryButton={
                  <FavoriteButton
                    artId={artworkId}
                    type="secondary"
                    size="medium"
                    animated={false}
                    className={artLinkDropdownWishlistCls}
                  />
                }
              />
            ) : (
              <FavoriteButton
                artId={artworkId}
                type="secondary"
                size="medium"
                width="container"
                showText
                animated={false}
              />
            )}
          </ArtActionRow>
        )}
        {/* Enquire Button */}
        {showEnquireButton ? (
          <ArtActionRow>
            <DropdownButton
              mainButton={
                <EnquireOverlay
                  title={enquireOverlayTitle}
                  component={({ onClose }) => (
                    <EnquireArtworkForm
                      onClose={onClose}
                      artwork={data}
                      selectedFrame={this.state.selectedFrame}
                      trigger={ART_ENUM.enquire.trigger.TRIGGER_ENQUIRE_BUTTON}
                    />
                  )}
                >
                  <ArtActionEnquire type="primary">
                    <FormattedMessage id="components.art.enquire_work" />
                  </ArtActionEnquire>
                </EnquireOverlay>
              }
              secondaryButton={
                <FavoriteButton
                  artId={artworkId}
                  type="primary"
                  size="medium"
                  animated={false}
                  className={artLinkDropdownWishlistCls}
                />
              }
            />
          </ArtActionRow>
        ) : null}
        {showCommission ? (
          <EnquireOverlay
            title={
              <FormattedMessage id="forms.enquireArtistCommission.title">
                {(label: string) => label}
              </FormattedMessage>
            }
            component={({ onClose }) => <EnquireCommissionForm onClose={onClose} artist={artist} />}
          >
            <Button type="primary" size="medium" width="container">
              <FormattedMessage id="components.art.commissionArtist" />
            </Button>
          </EnquireOverlay>
        ) : null}
        <ArtActionInfo>
          {urgencyData ? (
            <ArtActionInfoHighlighted
              icon={<ClockIcon />}
              title={urgencyData.heading}
              additional={urgencyData.content}
            />
          ) : null}
          {showCommission ? (
            <ArtActionInfoCommon icon={<TickIcon />}>
              {intl.formatMessage({ id: 'components.art.commissionLabel' })}{' '}
              <Link
                title={learnHowLabel}
                external
                target="_blank"
                href={LocalizedConfig.get('externalUrls.artCommissionsPage')}
              >
                {learnHowLabel}
              </Link>
            </ArtActionInfoCommon>
          ) : null}
          <ArtActionInfoCommon icon={<TickIcon />}>
            {intl.formatMessage({ id: 'components.art.returnsLabel' })}{' '}
            <Link
              title={learnMoreLabel}
              external
              target="_blank"
              href={LocalizedConfig.get('externalUrls.freeReturns')}
            >
              {learnMoreLabel}
            </Link>
          </ArtActionInfoCommon>
          {purchaseProps.canBuyInInstalments ? (
            <ArtActionInfoCommon
              icon={<TickIcon />}
              additional={
                purchaseProps.priceInInstalments ? (
                  <Fragment>
                    {' '}
                    {intl.formatMessage(
                      {
                        id: 'components.art.instalments.infoText',
                      },
                      { priceInInstalments: purchaseProps.priceInInstalments },
                    )}
                  </Fragment>
                ) : null
              }
            >
              {intl.formatMessage({ id: 'components.art.payInInstalments' })}{' '}
              <Link
                title={learnMoreLabel}
                external
                target="_blank"
                href={LocalizedConfig.get('externalUrls.instalmentsHelp')}
              >
                {learnMoreLabel}
              </Link>
            </ArtActionInfoCommon>
          ) : null}
          <ArtActionInfoCommon
            icon={<TickIcon />}
            additional={
              isProduct && productCanBuy ? (
                <ShipsFrom
                  date={
                    this.state.selectedFrame &&
                    this.state.selectedFrame.id &&
                    productPrimarySku.canBeFramed
                      ? productPrimarySku.shipsFramedBy
                      : productPrimarySku.shipsBy
                  }
                  countryCode={productPrimarySku.shipsFromCode}
                />
              ) : null
            }
          >
            {intl.formatMessage({ id: 'components.art.worldwideShipping' })}{' '}
            <Link
              title={learnMoreLabel}
              external
              target="_blank"
              href={LocalizedConfig.get('externalUrls.shippingHelp')}
            >
              {learnMoreLabel}
            </Link>
          </ArtActionInfoCommon>
          <ArtActionInfoCommon icon={<TickIcon />}>
            {intl.formatMessage(
              { id: 'components.art.trustedBy' },
              {
                link: (
                  <FormattedMessage id="brands.sohoHome">
                    {(label: string) => (
                      <Link
                        title={label}
                        external
                        target="_blank"
                        href={LocalizedConfig.get('externalUrls.sohoHome')}
                      >
                        {label}
                      </Link>
                    )}
                  </FormattedMessage>
                ),
              },
            )}
          </ArtActionInfoCommon>
          {shippingCountryCode === 'FR' ? (
            <ArtActionInfoCommon icon={<TickIcon />}>
              {intl.formatMessage(
                { id: 'components.art.kazoartProLeasing' },
                { strong: (chunks) => <strong>{chunks}</strong> },
              )}{' '}
              <Link
                title={learnMoreLabel}
                external
                target="_blank"
                href={LocalizedConfig.get('externalUrls.kazoartProLeasing')}
              >
                {learnMoreLabel}
              </Link>
            </ArtActionInfoCommon>
          ) : null}
        </ArtActionInfo>
      </ArtAction>
    );

    return (
      <MediaQuery minWidth={0}>
        <MediaQuery maxWidth={raScreenSmMax}>
          {CheckoutSection ? (
            <StickyScrollElement>
              <StickyScrollElementMain>{ArtActionSection}</StickyScrollElementMain>
              <StickyScrollElementFixed>
                <div className={artPurchaseRowCls}>
                  {CheckoutSection}
                  {BuyButton}
                </div>
              </StickyScrollElementFixed>
            </StickyScrollElement>
          ) : (
            ArtActionSection
          )}
        </MediaQuery>
        <MediaQuery minWidth={raScreenMd}>
          <StickyScrollParent>{ArtActionSection}</StickyScrollParent>
        </MediaQuery>
      </MediaQuery>
    );
  }
}

export const ArtDetails = withRouter(ArtDetailsComponent);
